"use client";

// WO-GF-001-P1B-S32 (F186) — THE CHANGE-MOUNTAIN DROPDOWN.
//
// FOUNDER RULING: a one-click mountain switch must not throw a full-screen selector at the visitor. The
// badge opens a SMALL dropdown; the map stays exactly one click away behind an "open the map" button.
// E3 (SiteHeaderHub, the 30 hub landings) and E4 (HubChromeLayer, ~361 sub-pages) ONLY — E1 mode tiles,
// E2 BOOK NOW, E2c hamburger, E5 content pill and E6b the transport map variant all stay full-selector.
//
// THIS PARTIALLY REVERSES S26, ON PURPOSE. S26 collapsed three divergent selectors into one because
// they had drifted apart unnoticed. The lesson was not "one component forever" — it was "one SOURCE OF
// TRUTH forever". So this dropdown owns no rules of its own: the row list, the pinned Whistler, the
// bookable/info-only split and the destination all come from the same modules the R1a selector uses.
// It is a second VIEW, never a second ruleset.
//
// PORTAL-TO-BODY, and why it is mandatory rather than tidy. The badge sits inside the hub chrome, which
// carries its own overflow and stacking contexts — the reason S26 had to delete a pile of RW2-5/S19
// in-tree anchoring hacks. Rendering into <body> and positioning from the trigger's own
// getBoundingClientRect sidesteps ancestor clipping entirely, which is the standard fix and the one the
// WO mandates. Do not re-anchor in-tree.
//
// KEYBOARD: W3C WAI-ARIA Authoring Practices, Menu Button pattern — aria-haspopup + aria-expanded on the
// trigger, Escape closes and RETURNS FOCUS to the trigger, arrow keys move within the menu.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { RESORTS, resortsActiveFirst } from "./resorts";
import { mountainCodeFromPath } from "./hub-nav";
import { requestDesignMountainMap } from "@/lib/mountain-select";
import { openMountainSelect } from "./MountainSelectLayer";
import type { MountainSelectMode } from "./MountainSelectModal";
import { destinationFor, intentFingerprint, readSwitchIntent, type SwitchIntent } from "./switch-intent";
import {
  bookabilityOf,
  COUNTRY_LABEL,
  COUNTRY_ORDER,
  groupByCountry,
  StatusLegend,
  StatusLight,
  useMountainCatalogue,
} from "./mountain-catalogue";

const ORANGE = "#f15a22";
const GREY = "#828282";
const RULE = "#e2e2e2";
// S35 · F226 — 268 → 292. The row now carries a status light and a country it did not before; keeping
// the old width would have bought the grouping by squeezing the NAME, and a truncated mountain name in
// a mountain switcher is a worse defect than the one being fixed.
const MENU_W = 292;

export default function MountainSwitchMenu({
  open,
  anchor,
  mode = null,
  onClose,
}: {
  open: boolean;
  /** the element the menu positions itself against — its rect is the only geometry input */
  anchor: HTMLElement | null;
  mode?: MountainSelectMode;
  onClose: () => void;
}) {
  const [intent, setIntent] = useState<SwitchIntent | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // THE SERVED SIGNAL, REUSED — NOT RE-DERIVED. S31 built /api/marketplace/bookable precisely so that
  // "which mountains have product" has one answer. Re-deriving it here (or hardcoding a list) would
  // recreate the exact defect F184 existed to kill, one component over. `null` stays "unknown" and
  // renders no light and no chip: an unknown must never render as a green positive.
  //
  // S35 · F226 — and it is no longer fetched here at all. The list metadata (which carries the SERVED
  // country), the bookability set and the readiness facts all come from `useMountainCatalogue`, the
  // same hook the R1a selector reads. That is the founder's "one source, no second implementation"
  // rail made structural: this file cannot classify a mountain into Canada or paint a light green by a
  // rule of its own, because it no longer owns either rule.
  const { meta, bookable, metaLoaded, bookabilityLoaded } = useMountainCatalogue(open);

  // the intent is captured when the menu OPENS — i.e. the state the visitor was actually in — so a
  // later re-render cannot quietly change what is being preserved.
  // Snapshotted during render (React's "adjust state when a prop changes" pattern) rather than in an
  // effect: same trigger — open turning on, or mode changing while open — without a cascading re-render.
  const [snapshotOf, setSnapshotOf] = useState<{ open: boolean; mode: MountainSelectMode } | null>(null);
  if (snapshotOf?.open !== open || snapshotOf?.mode !== mode) {
    setSnapshotOf({ open, mode });
    if (open) setIntent(readSwitchIntent(mode));
  }

  const place = useCallback(() => {
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    // clamp into the viewport so the menu can never render off-screen on a narrow window — the F172
    // class of defect (a control that exists but cannot be reached).
    const left = Math.max(8, Math.min(r.left, window.innerWidth - MENU_W - 8));
    setPos({ top: r.bottom + 6, left });
  }, [anchor]);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  // Escape closes AND returns focus to the trigger (APG); outside-click closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        anchor?.focus?.();
      }
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(t) && anchor && !anchor.contains(t)) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, onClose, anchor]);

  // ONE ORDERING RULE, taken from the same helper the R1a selector uses, then Whistler pinned on top —
  // the S31 F183 shape: the pin is a SHORTCUT, so Whistler still appears in the body of the list.
  // S35 · F226 — US/CAN SEGMENTATION, from the SHARED grouper. The ordering handed in is still the F125
  // active-first rule; `groupByCountry` only buckets. A mountain whose served region has not arrived
  // classifies as "Other" for those frames and moves into its country when it does — the same transient
  // the R1a accordions have always had, and the reason `data-switch-bookability-loaded` is published.
  const rows = useMemo(() => {
    const whistler = RESORTS.find((r) => mountainCodeFromPath(r.href) === "whistler");
    return { whistler: whistler ?? null, groups: groupByCountry(resortsActiveFirst(), meta) };
  }, [meta]);

  if (!open || !pos || !intent) return null;

  const row = (r: { href: string; label: string }, pinned = false) => {
    const code = mountainCodeFromPath(r.href) || "";
    const status = bookabilityOf(code, bookable);
    const isBookable = status === "bookable";
    const href = destinationFor(r.href, intent);
    return (
      <a
        key={(pinned ? "pin:" : "row:") + r.href}
        href={href}
        role="menuitem"
        data-switch-row={r.label.trim()}
        data-code={code}
        data-bookability={status}
        // the fingerprint rides on the row itself, so the gate reads the intent that this control will
        // actually navigate with — not one recomputed by the test.
        data-switch-intent={intentFingerprint(intent)}
        className="sbHoverRow"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          // S35 · F226 — "better visual separation between rows". 7px → 11px vertical: at 27px tall with
          // a hairline between them, thirty rows read as a wall of text, which is the founder's "poor".
          padding: "11px 12px",
          textDecoration: "none",
          color: "#222",
          fontSize: 13,
          // the separator is the ROW's, not the group's, so the last row of Canada and the first row of
          // USA are divided by the sticky header rather than by two stacked rules.
          borderBottom: `1px solid ${RULE}`,
        }}
      >
        {/* S35 · F206/F226 — THE SAME LIGHT THE SELECTOR DRAWS, from the same component. Green =
            bookable, yellow = live but info-only, and an UNKNOWN draws nothing at all. */}
        <StatusLight status={status} size={9} />
        <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {r.label.trim()}
        </span>
        {/* the same claim the selector makes, from the same source: BOOKABLE means "has served product" */}
        {isBookable ? (
          <span data-switch-bookable-chip style={{ fontSize: 9, fontWeight: 700, color: "#2a9d8f", letterSpacing: ".04em" }}>
            BOOKABLE
          </span>
        ) : null}
      </a>
    );
  };

  // S35 · F208/F226 — STICKY COUNTRY HEADERS, "exactly like F208". `position: sticky` against the
  // scroll container below: Canada pins to the top while its rows scroll under it until USA reaches the
  // top and pushes it out of the way. It is a plain sticky header rather than the selector's collapsible
  // accordion on purpose — this menu is a ONE-CLICK switch (the whole point of F186), and a control that
  // can hide the country you are looking for is the opposite of that. The map button below still opens
  // the full selector for anyone who wants the accordions.
  const countryHeader = (country: (typeof COUNTRY_ORDER)[number], count: number) => (
    <div
      data-switch-country-header={country}
      style={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        background: "#f6f7f8",
        borderBottom: `1px solid ${RULE}`,
        padding: "6px 12px",
        fontSize: 10,
        fontWeight: 700,
        color: "#333",
        textTransform: "uppercase",
        letterSpacing: ".5px",
      }}
    >
      {COUNTRY_LABEL[country]} <span style={{ color: GREY, fontWeight: 500 }}>({count})</span>
    </div>
  );

  return createPortal(
    <div
      ref={menuRef}
      data-mountain-switch-menu
      data-switch-intent={intentFingerprint(intent)}
      // S35 card-1b — the same readiness facts the R1a selector publishes, for the same reason: both
      // the country a row sits under and the light it carries arrive on a fetch, so a gate that reads
      // this menu the instant it is visible reads a list that is about to reshape.
      data-switch-meta-loaded={metaLoaded ? "true" : "false"}
      data-switch-bookability-loaded={bookabilityLoaded ? "true" : "false"}
      role="menu"
      aria-label="Change mountain"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: MENU_W,
        maxHeight: "min(60vh, 420px)",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        border: `1px solid #d9d9d9`,
        borderRadius: 10,
        boxShadow: "0 8px 28px rgba(0,0,0,.16)",
        zIndex: 2200,
        overflow: "hidden",
        fontFamily: '"Roboto", system-ui, sans-serif',
      }}
    >
      <div style={{ overflowY: "auto", flex: 1 }}>
        {rows.whistler ? (
          <div data-switch-pinned="whistler">
            <div style={{ fontSize: 10, fontWeight: 700, color: GREY, textTransform: "uppercase", letterSpacing: ".5px", padding: "8px 12px 4px" }}>
              Featured
            </div>
            {row(rows.whistler, true)}
          </div>
        ) : null}
        {COUNTRY_ORDER.map((country) =>
          rows.groups[country].length ? (
            <div key={country} data-switch-country={country}>
              {countryHeader(country, rows.groups[country].length)}
              {rows.groups[country].map((r) => row(r))}
            </div>
          ) : null,
        )}
      </div>
      {/* S36 card 1 · F251 — the HUD dropdown shows the same lamps, so it says the same words. `compact`
          because the panel is 268px wide; the vocabulary is not a prop. */}
      <div
        data-switch-legend
        style={{ flex: "0 0 auto", borderTop: `1px solid ${RULE}`, padding: "8px 12px", background: "#fff" }}
      >
        <StatusLegend compact />
      </div>
      {/* THE MAP IS ONE CLICK AWAY — the founder's ruling is that the switch is small by default, not
          that the map is gone. This opens the same R1a selector every other entry point opens, carrying
          the SAME mode, so the map path and this path resolve identically. */}
      <button
        type="button"
        data-switch-open-map
        onClick={() => {
          // Kit 2.01: where a design panel is mounted, "Open the map" is its map mode. On routes with no design
          // panel the shared legacy modal is PRESERVED as the functional fallback for now (not eliminated), so
          // the control is never dead.
          if (requestDesignMountainMap(mountainCodeFromPath(window.location.pathname) ?? null)) onClose();
          else {
            onClose();
            openMountainSelect(mode);
          }
        }}
        style={{
          borderTop: `1px solid ${RULE}`,
          background: "#fff",
          color: ORANGE,
          fontWeight: 700,
          fontSize: 12,
          padding: "10px",
          cursor: "pointer",
          textAlign: "center",
          flex: "0 0 auto",
        }}
      >
        Open the map
      </button>
    </div>,
    document.body,
  );
}
